import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';

import {
  clearJWTToken,
  getImpersonatorJWTToken,
  getJWTToken,
} from 'src/utils/auth';
import { SSO_AUTH_URL } from 'src/constants/auth';
import {
  CurrentUserQuery,
  CurrentUserDocument,
  User,
} from '../graphql/generated/graphql';
import { getIsOtpPro } from 'src/utils/user-data';

export interface AuthContextType {
  user?: User;
  loading: boolean;
  logout: () => void;
  isAdmin: boolean;
  isOtpPro: boolean;
  isImpersonating: boolean;
  loadUser: () => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

function useAuthContext() {
  return useContext(AuthContext);
}

function AuthContextProvider({ children }: { children?: React.ReactNode }) {
  const [user, setUser] = useState<User | undefined>();
  const [impersonatorJWTToken, setImpersonatorJWTToken] = useState<
    string | null
  >(null);

  const { loading, refetch } = useQuery<CurrentUserQuery>(CurrentUserDocument, {
    skip: true,
    errorPolicy: 'ignore',
  });

  const isAdmin = !!user?.permissions?.find(
    (permission) => permission.appArea === 'Admin', // ToDo: check this logic and align with BE logic (PermissionName.Admin),
  );
  const isOtpPro = getIsOtpPro(user);
  const isImpersonating = !!impersonatorJWTToken;

  const loadUser = useCallback(async () => {
    const { data: userData } = await refetch();
    setUser(userData?.currentUser as User);
  }, [refetch]);

  useEffect(() => {
    (async () => {
      if (!getJWTToken()) {
        window.location.href = SSO_AUTH_URL || '/';
        return;
      }

      await loadUser();
      setImpersonatorJWTToken(getImpersonatorJWTToken());
    })();
  }, [loadUser]);

  const logout = () => {
    clearJWTToken();
    setUser(undefined);
  };

  const value: AuthContextType = {
    user,
    isAdmin,
    isOtpPro,
    isImpersonating,
    loading,
    logout,
    loadUser,
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={value}>
      {!user ? null : children}
    </AuthContext.Provider>
  );
}

export { AuthContextProvider, useAuthContext };
